import React from "react";
import JsxSyntaxHighlighter from "../../ui/JsxSyntaxHighlighter";
import ApiLink from "../../ui/ApiLink";

export default function UseProFile() {
  return (
    <>
      <JsxSyntaxHighlighter>
        {`useProFile(config: MinibeakConfig): MinibeakHookResult`}
      </JsxSyntaxHighlighter>

      <p>
        React hook to fetch data from ProFile's json-api. Refreshes
        automatically when config changes. Must be refreshed manually when
        resources are modified, by calling <code>.refresh()</code> on the
        returned object.
      </p>

      <p>
        Returns a <ApiLink name="MinibeakHookResult" />.
      </p>

      <p>
        Pass the full config, including <code>apiUrl</code>, on every call. The
        usual way is to spread <ApiLink name="defaultConfig" /> or your own
        frozen config object built from it. Nothing is merged in implicitly.
      </p>

      <p>
        A failed request ends up in <code>result.error</code>. Set{" "}
        <code>throwErrors: true</code> in the config to have the hook throw the
        error during render instead, so the nearest React error boundary catches
        it. Either way <code>refresh()</code> resolves and never rejects.
      </p>

      <p>
        <code>useProFile</code> is only intended to be used to GET resources. If
        you need to create, update, delete or do some other action, use{" "}
        <ApiLink name="callProFile" /> instead.
      </p>

      <p>
        <code>useProFile</code> uses <ApiLink name="callProFile" /> and{" "}
        <ApiLink name="processProFileResult" /> internally.
      </p>

      <h3>Example code:</h3>

      <JsxSyntaxHighlighter>
        {`// Inside your functional component:

const achievementTypes = useProFile<AchievementTypeResource[]>({
  ...defaultConfig,
  path: "/achievementTypes/editable",
  queryParams: {
    max,
    offset,
    sort: orderByField + " " + (orderReverse ? "DESC" : "ASC"),
    filter: debouncedFilterString
  },
  include: [
    "shares",
    "drafts",
    "usedInPlanTypesCount",
    "category",
    "orgUnit"
  ]
});

if (!achievmentTypes.isReady) {
  return <LoadingComponent/>;
}

// For pagination:
const totalCount = achievementTypes.response?.links.total;

console.log("Result of call, with linked resources:", achievmentTypes.data);
console.log("All included resources by type and id:", achievmentTypes.resources);

// to refresh, call achievementTypes.refresh()

`}
      </JsxSyntaxHighlighter>
    </>
  );
}
